import { SphereIconButton } from "@components/common/sphere-icon-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import {
  blue,
  CloseIcon,
  green,
  red,
  CheckmarkCircleFillIcon,
  XMarkCircleFillIcon,
} from "@flat-ui";
import { CircularProgress, Grid2, SvgIcon } from "@mui/material";
import { useAppDispatch } from "@store/store-helper";
import { removeOneManually } from "@store/table/table-slice";
import { FetchingItem } from "@store/table/table-slice-helper";
import { getSvgColoredIconCss } from "@utils/ui-utils";

/** Size of the status icon in pixels */
const ICON_SIZE_PX = 20;

interface Props {
  /** The item itself */
  item: FetchingItem;
}

/** Shows appropriate icon based on the fetching status */
export function ListStatusIcon({ item }: Props): React.ReactNode {
  const dispatch = useAppDispatch();
  let statusIcon: React.ReactNode = null;

  switch (item.status) {
    case "idle":
      statusIcon = (
        <SphereTooltip title="Remove from this list">
          <SphereIconButton
            component={CloseIcon}
            onClick={() => dispatch(removeOneManually(item.id))}
            iconSize={`${ICON_SIZE_PX}px`}
          />
        </SphereTooltip>
      );
      break;

    case "in-queue":
      statusIcon = (
        <CircularProgress
          size={`${ICON_SIZE_PX}px`}
          sx={{
            color: blue[200],
            margin: "8px",
          }}
        />
      );
      break;

    case "updating":
      statusIcon = (
        <CircularProgress
          size={`${ICON_SIZE_PX}px`}
          sx={{
            color: blue[500],
            margin: "8px",
          }}
        />
      );
      break;

    case "success":
      statusIcon = (
        <SvgIcon
          inheritViewBox
          component={CheckmarkCircleFillIcon}
          sx={{
            ...getSvgColoredIconCss(green[700]),
            height: `${ICON_SIZE_PX}px`,
            width: `${ICON_SIZE_PX}px`,
            margin: "8px",
          }}
        />
      );
      break;

    case "error":
      statusIcon = (
        <SvgIcon
          inheritViewBox
          component={XMarkCircleFillIcon}
          sx={{
            ...getSvgColoredIconCss(red[500]),
            height: `${ICON_SIZE_PX}px`,
            width: `${ICON_SIZE_PX}px`,
            margin: "8px",
          }}
        />
      );
  }

  return (
    <Grid2 container alignItems="center">
      {item.message && (
        <SphereTooltip
          title={item.message}
          boxProps={{
            sx: {
              color: blue[500],
              fontSize: "12px",
              mr: "20px",
              cursor: "pointer",
            },
          }}
        >
          View info
        </SphereTooltip>
      )}
      {statusIcon}
    </Grid2>
  );
}
